import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { IMatch } from '@/interface/IMatch';
import { MarketPage, SportType } from '@/interface/enum';
import { remove, upsert } from '@/libraries/array';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import BusinessLogicHelper from '@/libraries/helpers/businessLogicHelper';
import { eventResultScoreBetTypeGroupIds } from '@/libraries/oddsApi/betBuilderScoreSource';
import { getFilter } from '@/libraries/oddsApi/helpers';
import { formatEvent } from '@/libraries/oddsApi/oddsApiFormatter';
import { sortMatches } from '@/libraries/oddsDisplay';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useOddsApiStore } from '@/stores/oddsApiStore';
import { useRootStore } from '@/stores/rootStore';
import type { EventTypeFragment } from '@/types';
import { EnumEventDateFilterType, EnumOddsCategoryType, EnumPresetFilterType, EnumTimeZoneType, useEventsQuery as _useEventsQuery, useEventsSubscription } from '@/types';

interface UseEventsQueryProps {
    sportType: MaybeRef<SportType>;
    marketPage: MaybeRef<MarketPage>;
    isMixParlayMode: MaybeRef<boolean>;
    isIncludeLiveInToday?: MaybeRef<boolean | undefined>;
    tournamentIds?: MaybeRef<number[] | undefined>;
    tournamentNames?: MaybeRef<string[] | undefined>;
    isEnabled?: MaybeRef<boolean | undefined>;
    isSubScriptionEnabled?: MaybeRef<boolean | undefined>;
}

export function useEventsQuery({
    sportType,
    marketPage,
    isMixParlayMode,
    isIncludeLiveInToday = false,
    tournamentIds = undefined,
    tournamentNames = undefined,
    isEnabled: _isEnabled = true,
    isSubScriptionEnabled = true,
}: UseEventsQueryProps) {
    const events = ref<IMatch[]>([]);

    const variable = deepCompareComputed(() => ({
        query: {
            sport: SportType.toOddsApi(get(sportType)),
            filter: get(isIncludeLiveInToday) && get(marketPage) === MarketPage.Today
                ? { presetFilter: EnumPresetFilterType.All, date: EnumEventDateFilterType.TodayExpanded }
                : getFilter(get(marketPage)),
            timeZone: EnumTimeZoneType.UTC_8,
            oddsCategory: get(isMixParlayMode) ? EnumOddsCategoryType.MixParlay : EnumOddsCategoryType.All,
            tournamentIds: get(tournamentIds),
            tournamentNames: get(tournamentNames),
        },
        betTypeGroupIds: eventResultScoreBetTypeGroupIds,
    }));

    const { customerSettings } = storeToRefs(useOddsApiStore());
    const isEnabled = computed(() => {
        if (!BusinessLogicHelper.isAllowedSport(customerSettings.value, SportType.toOddsApi(get(sportType)))) {
            return false;
        }
        return get(_isEnabled);
    });

    const { isWsConnected } = storeToRefs(useRootStore());

    const pollInterval = computed(() => {
        if (isWsConnected.value) {
            return undefined;
        }
        return 30000;
    });

    const { onResult, refetch, loaded } = _useEventsQuery(
        variable,
        deepCompareComputed(() => ({
            enabled: isEnabled.value,
            pollInterval: get(pollInterval),
        })),
    );

    const { filterEFootballLeagueIds } = storeToRefs(useFeatureToggleStore());

    function preserveDynamicMarketCount(event: EventTypeFragment): EventTypeFragment {
        const existingDynamicMarketCount = events.value.find(existingEvent => existingEvent.eventId === event.id)?.dynamicMarketCount;
        return {
            ...event,
            dynamicMarketCount: event.dynamicMarketCount ?? existingDynamicMarketCount,
        };
    }

    onResult((result) => {
        const filtered = result?.filter(event => BusinessLogicHelper.isEventAllowed(customerSettings.value, event, filterEFootballLeagueIds.value))
            .map(event => formatEvent(event)) ?? [];
        events.value = sortMatches(filtered);
    });

    const { onSubscription } = useEventsSubscription(
        variable,
        deepCompareComputed(() => ({
            enabled: isEnabled.value && get(isSubScriptionEnabled),
        })),
    );

    onSubscription((result) => {
        if (!result) {
            return;
        }

        upsert(events.value, result.updated
            .filter(event => BusinessLogicHelper.isEventAllowed(customerSettings.value, event, filterEFootballLeagueIds.value))
            .map(preserveDynamicMarketCount)
            .map(formatEvent), item => item.eventId);

        remove(events.value, result.deleted, (oldMatch, deletedMatch) => oldMatch.eventId === deletedMatch);

        events.value = sortMatches(events.value);
    });

    watch(variable, () => {
        events.value = [];
    });

    return {
        events,
        loaded,
        refetch,
    };
}
